import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { message } from "antd";

import { StudentAction, MeetingAction } from "../../store/actions";

import {
  meeting,
  boxContainer,
  Spacer,
  inputFields,
  Loader,
  datepicker,
  upload,
  Buttons,
} from "../../components";
import { Images, Utils } from "../../config";
import TimeSlots from "../../config/utility-arrays/timeslots";

const RescheduleMeeting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const profileData = useSelector((state) => state.profile?.user);
  const schoolId =
    profileData?.role === "school"
      ? profileData?.schoolId
      : profileData?.staff?.schoolId;
  const { activeStudents, isLoading } = useSelector((state) => state.students);
  const meetingLoading = useSelector((state) => state.meeting?.isLoading);
  const timeslots = useSelector((state) => state.meeting?.timeslots);
  const timeslotLoading = useSelector(
    (state) => state.meeting?.isTimeslotLoading
  );

  const [buttonText, setButtonText] = useState("Reschedule");
  const [studentList, setStudentList] = useState([]);
  const [timeSlot, setTimeSlot] = useState([]);

  const [state, setState] = useState({
    meetingId: "",
    subject: "",
    meetingDate: null,
    meetingDatePrev: "",
    timeSlot: null,
    timeSlotPrev: null,
    message: "",
    meetingType: "",
    studentUserId: null,
    meetingLink: "",
    meetingFile: "",
  });

  useEffect(() => {
    setState({
      ...state,
      meetingId: location?.state?.id,
      subject: location?.state?.subject,
      meetingDate: location?.state?.meetingDate,
      meetingDatePrev: location?.state?.meetingDate,
      timeSlot: location?.state?.timeSlot?.id,
      timeSlotPrev: location?.state?.timeSlot?.id,
      message: location?.state?.message,
      meetingType: location?.state?.meetingType,
      studentUserId: location?.state?.studentUserId,
      meetingLink: location?.state?.meetingLink,
      meetingFile: location?.state?.meetingFile,
    });
  }, [location?.state]);

  useEffect(() => {
    if (activeStudents.length === 0) {
      const payload = { schoolId };
      dispatch(StudentAction.getActiveStudents(payload));
    }
  }, [profileData]);

  useEffect(() => {
    if (profileData?.id && state?.meetingDate) {
      const payload = {
        userId: profileData?.id,
        date: state?.meetingDate,
      };
      dispatch(MeetingAction.getTimeslots(payload));
    }
  }, [state?.meetingDate, profileData]);

  useEffect(() => {
    const filteredTimeslots = timeslots.map((timeslot) => ({
      label: timeslot?.timeSlot,
      value: timeslot?.id,
    }));
    setTimeSlot(filteredTimeslots);
  }, [timeslots]);

  useEffect(() => {
    const filteredStudents = activeStudents.map((student) => ({
      label: `${student?.firstName} ${student?.lastName}`,
      value: student?.id,
    }));
    setStudentList(filteredStudents);
  }, [activeStudents]);

  const handleSubmit = () => {
    if (
      !state?.meetingDate ||
      !state?.timeSlot ||
      !state?.meetingType ||
      !state?.meetingLink
    ) {
      message.error("Please fill all the required fields.");
    } else if (
      state?.meetingType === "online" &&
      state?.meetingLink &&
      !Utils?.isValidURL(state.meetingLink)
    ) {
      message.error("Please enter a valid meeting link.");
    } else {
      setButtonText("Rescheduling");
      const payload = {
        meetingId: state?.meetingId,
        timeSlot: state?.timeSlot,
        timeSlotPrev: state?.timeSlotPrev,
        meetingType: state?.meetingType,
        meetingLink: state?.meetingLink,
        meetingDate: state?.meetingDate,
        meetingDatePrev: state?.meetingDatePrev,
        message: state?.message,
      };
      dispatch(
        MeetingAction.rescheduleMeeting(payload, (res) => {
          if (res.status === 200) {
            dispatch(
              MeetingAction.getUpcomingMeetings({
                requestedBy: "school",
                schoolId: profileData?.schoolId ?? profileData?.staff?.schoolId,
                limit: 10,
                offset: 0,
              })
            );
            navigate("/meetings/received");
          }
          setButtonText("Reschedule");
        })
      );
    }
  };

  const radioItems = [
    { label: "In Person", value: "offline" },
    { label: "Online", value: "online" },
  ];

  console.log(state);

  return (
    <div className="text-black">
      <div className="header-md pl-3">Reschedule Meeting</div>
      <Spacer height={20} />
      <boxContainer.LiteBoxContainer>
        <div className="text-[1.25em] font-semibold">
          Fill in your details and schedule a meeting for any guidance regarding
          admissions from your College counsellor.
        </div>
        <Spacer height={25} />
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 opacity-50 pointer-events-none">
            <meeting.AddMeetingBoxLabel
              title="Select Counselor"
              required={true}
            />
            {isLoading ? (
              <Loader.Circular />
            ) : (
              <inputFields.SelectDropdown
                items={studentList}
                value={state?.studentUserId}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 opacity-50 pointer-events-none">
            <meeting.AddMeetingBoxLabel title="Subject" required={true} />
            <inputFields.DefaultInput value={state.subject} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-2">
              <meeting.AddMeetingBoxLabel title="Select Date" required={true} />
              <datepicker.DefaultDatepicker
                datevalue={state?.meetingDate}
                onChange={(date, dateString) =>
                  setState({
                    ...state,
                    meetingDate: dateString,
                    timeSlot: null,
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <meeting.AddMeetingBoxLabel
                title="Select Timeslot"
                required={true}
              />
              <inputFields.SelectDropdown
                items={timeSlot}
                onChange={(e) => setState({ ...state, timeSlot: e })}
                value={state?.timeSlot}
                placeholder="Select Time Slot"
                loading={timeslotLoading}
              />
            </div>
            <div className="flex flex-col gap-2">
              <meeting.AddMeetingBoxLabel title="Document Attachment" />
              {state.meetingFile ? (
                <Link to={state?.meetingFile} target="_blank">
                  <div className="default-file-input">
                    <div className={`opacity-[100%] text-[0.875em]`}>
                      File uploaded
                    </div>
                    <div>
                      <img
                        src={Images.DocumentAttachment}
                        className="h-[1.125em] pr-2"
                      />
                    </div>
                  </div>
                </Link>
              ) : (
                <upload.DefaultFileUpload
                  onChange={(file) => setState({ ...state, meetingFile: file })}
                  value={state.meetingFile}
                  icon={Images.DocumentAttachment}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <meeting.AddMeetingBoxLabel title="Message" />
            <inputFields.DefaultTextbox
              onChange={(e) => setState({ ...state, message: e.target.value })}
              value={state.message}
            />
          </div>
          <div className="flex flex-col gap-2">
            <meeting.AddMeetingBoxLabel
              title="Select Mode of Meeting"
              required={true}
            />
            <div className="flex gap-[100px] items-center">
              <div className="pl-2">
                <inputFields.VerticalRadio
                  items={radioItems}
                  onChange={(e) =>
                    setState({ ...state, meetingType: e.target.value })
                  }
                  value={state.meetingType}
                />
              </div>
              {state.meetingType && (
                <div className="flex flex-col gap-2 w-[500px]">
                  <meeting.AddMeetingBoxLabel
                    title={
                      state.meetingType === "offline"
                        ? "Add Location"
                        : "Attach Link"
                    }
                    required={true}
                  />
                  <inputFields.DefaultInput
                    onChange={(e) =>
                      setState({ ...state, meetingLink: e.target.value })
                    }
                    value={state.meetingLink}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <Spacer height={30} />
        <div className="flex w-full justify-end">
          <Buttons.DefaultButton
            disabled={meetingLoading}
            onClick={handleSubmit}
            loading={meetingLoading}
          >
            {buttonText}
          </Buttons.DefaultButton>
        </div>
      </boxContainer.LiteBoxContainer>
    </div>
  );
};

export default RescheduleMeeting;
